/**
 * Sozialpass — Zähl- und Status-Logik (OU-2a).
 *
 * Fachlich: SuS leisten pro Schuljahr eine feste Zahl an „Sozialpassstunden"
 * (Default 4). Ein Eintrag == genau EINE Stunde — es gibt keine Teilmengen und
 * keine Stundenzahl pro Eintrag; wer zwei Stunden am Stück leistet, bekommt
 * zwei Einträge. Zusätzlich müssen die ersten `requiredFirstHalf` Stunden
 * (Default 2) bereits im 1. Halbjahr erbracht sein.
 *
 * Diese Datei ist PURE (CLAUDE.md §26): keine DB, keine Date-„jetzt"-Quelle.
 * Alle Zeitpunkte kommen als "YYYY-MM-DD"-Strings herein (Berlin-Datum, vom
 * Aufrufer via `formatDateStr(timeSource.now())` bzw. aus den `Schoolyear`-
 * Grenzen abgeleitet), damit Vergleiche lexikographisch und TZ-frei bleiben.
 *
 * Warn-Semantik (Feature-Request 2026-09):
 *   - Jahresziel erfüllt  → Status IMMER `erfuellt`, jede Warnung verstummt.
 *     Auch dann, wenn das HJ1-Ziel seinerzeit verfehlt wurde: der Rückstand
 *     ist aufgeholt, ein dauerhafter Pranger wäre pädagogisch sinnlos.
 *   - Sonst `warnung`, sobald `warnDaysBefore` Tage vor dem jeweiligen Stichtag
 *     erreicht sind, `ueberfaellig` ab dem Tag NACH dem Stichtag.
 *   - Der HJ1-Stichtag hat Vorrang vor dem Jahres-Stichtag, solange das
 *     HJ1-Ziel offen ist — er kommt zuerst und ist die konkretere Aussage.
 */
export const SOZIALPASS_DEFAULTS = {
    /** Pflicht-Stunden pro Schuljahr. */
    REQUIRED_PER_YEAR: 4,
    /** Davon bis zum Ende des 1. Halbjahres zu leisten. */
    REQUIRED_FIRST_HALF: 2,
    /** Vorwarnzeit in Tagen vor dem jeweiligen Stichtag. */
    WARN_DAYS_BEFORE: 28,
};
export const SOZIALPASS_BOUNDS = {
    REQUIRED_PER_YEAR: { MIN: 0, MAX: 20 },
    REQUIRED_FIRST_HALF: { MIN: 0, MAX: 20 },
    WARN_DAYS_BEFORE: { MIN: 0, MAX: 120 },
};
/** Maximale Länge der Tätigkeitsbeschreibung („was gemacht wurde"). */
export const SOZIALPASS_DESCRIPTION_MAX = 500;
export const SOZIALPASS_STATUS = {
    /** Jahresziel erreicht. */
    ERFUELLT: 'erfuellt',
    /** Noch offen, aber kein Stichtag in Sicht. */
    OFFEN: 'offen',
    /** Stichtag rückt näher (< `warnDaysBefore` Tage). */
    WARNUNG: 'warnung',
    /** Stichtag verstrichen, Soll nicht erreicht. */
    UEBERFAELLIG: 'ueberfaellig',
};
export const SOZIALPASS_STATUS_VALUES = Object.values(SOZIALPASS_STATUS);
export const SOZIALPASS_STATUS_LABELS = {
    [SOZIALPASS_STATUS.ERFUELLT]: 'Erfüllt',
    [SOZIALPASS_STATUS.OFFEN]: 'Offen',
    [SOZIALPASS_STATUS.WARNUNG]: 'Frist nah',
    [SOZIALPASS_STATUS.UEBERFAELLIG]: 'Überfällig',
};
/** Barrierefreies Symbol je Status (CLAUDE.md §6 — nie nur Farbe). */
export const SOZIALPASS_STATUS_ICONS = {
    [SOZIALPASS_STATUS.ERFUELLT]: '✓',
    [SOZIALPASS_STATUS.OFFEN]: '○',
    [SOZIALPASS_STATUS.WARNUNG]: '⏰',
    [SOZIALPASS_STATUS.UEBERFAELLIG]: '✗',
};
/**
 * Verschiebt ein "YYYY-MM-DD" um `days` Tage. Rechnet bewusst in UTC
 * (`Date.UTC`), also ohne DST-Sprünge — es geht um reine Kalendertag-Arithmetik,
 * nicht um Instants.
 */
export function shiftYmd(ymd, days) {
    const [y, m, d] = ymd.split('-').map(Number);
    const shifted = new Date(Date.UTC(y, m - 1, d + days));
    return shifted.toISOString().slice(0, 10);
}
/**
 * Der Sozialpass-Status eines/einer Schüler:in für EIN Schuljahr.
 *
 * Reihenfolge der Prüfungen ist bedeutsam — siehe Datei-Doc oben.
 */
export function computeSozialpassStatus(input) {
    const { counts, today, hj1End, yearEnd, requiredPerYear, requiredFirstHalf, warnDaysBefore } = input;
    const missingTotal = Math.max(0, requiredPerYear - counts.total);
    const missingFirstHalf = Math.max(0, requiredFirstHalf - counts.firstHalf);
    // Jahresziel erreicht → alles gut, auch bei seinerzeit verfehltem HJ1-Ziel.
    if (missingTotal === 0) {
        return {
            status: SOZIALPASS_STATUS.ERFUELLT,
            reason: null,
            missingTotal: 0,
            missingFirstHalf: 0,
            dueDate: null,
        };
    }
    // Schuljahr vorbei und Soll nicht erreicht — härteste Aussage, zuerst.
    if (today > yearEnd) {
        return {
            status: SOZIALPASS_STATUS.UEBERFAELLIG,
            reason: 'jahr',
            missingTotal,
            missingFirstHalf,
            dueDate: yearEnd,
        };
    }
    // HJ1-Dimension — nur solange das HJ1-Ziel offen ist.
    if (missingFirstHalf > 0) {
        if (today > hj1End) {
            return {
                status: SOZIALPASS_STATUS.UEBERFAELLIG,
                reason: 'hj1',
                missingTotal,
                missingFirstHalf,
                dueDate: hj1End,
            };
        }
        if (today >= shiftYmd(hj1End, -warnDaysBefore)) {
            return {
                status: SOZIALPASS_STATUS.WARNUNG,
                reason: 'hj1',
                missingTotal,
                missingFirstHalf,
                dueDate: hj1End,
            };
        }
    }
    // Jahres-Dimension.
    if (today >= shiftYmd(yearEnd, -warnDaysBefore)) {
        return {
            status: SOZIALPASS_STATUS.WARNUNG,
            reason: 'jahr',
            missingTotal,
            missingFirstHalf,
            dueDate: yearEnd,
        };
    }
    return {
        status: SOZIALPASS_STATUS.OFFEN,
        reason: null,
        missingTotal,
        missingFirstHalf,
        dueDate: null,
    };
}
/** Zählt Einträge auf Halbjahre auf — `workDate <= hj1End` zählt als 1. HJ. */
export function countSozialpassEntries(workDates, hj1End) {
    let firstHalf = 0;
    for (const d of workDates) {
        if (d <= hj1End)
            firstHalf += 1;
    }
    return { firstHalf, secondHalf: workDates.length - firstHalf, total: workDates.length };
}
/** Kurzer deutscher Warntext für Badges/Tooltips. */
export function sozialpassStatusHint(result) {
    switch (result.status) {
        case SOZIALPASS_STATUS.ERFUELLT:
            return 'Alle Sozialpassstunden geleistet.';
        case SOZIALPASS_STATUS.WARNUNG:
            return result.reason === 'hj1'
                ? `Noch ${result.missingFirstHalf} Stunde(n) bis zum Ende des 1. Halbjahres.`
                : `Noch ${result.missingTotal} Stunde(n) bis zum Schuljahresende.`;
        case SOZIALPASS_STATUS.UEBERFAELLIG:
            return result.reason === 'hj1'
                ? `${result.missingFirstHalf} Stunde(n) aus dem 1. Halbjahr fehlen.`
                : `${result.missingTotal} Stunde(n) aus dem Schuljahr fehlen.`;
        default:
            return `Noch ${result.missingTotal} Stunde(n) offen.`;
    }
}
//# sourceMappingURL=sozialpass.js.map